import React, { useEffect, useState } from "react";
import MainNavbar from "../components/navbar";
import { loadFromLocalStorage } from "../utils";
import { activityLog } from "../objects/activityLog";
import { archivedSite } from "../objects/archivedSite";
import { apiScopes } from "../authConfig";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { getOwnedArchivedResources } from "../services/resource-manager";
import Swal from "sweetalert2";
import { errorsTypes } from "../errorTypes";
import { InteractionStatus } from "@azure/msal-browser";
import {
  DirectionalHint,
  Icon,
  MessageBar,
  MessageBarType,
  ProgressIndicator,
  SearchBox,
  Shimmer,
  ShimmerElementType,
  Spinner,
  SpinnerSize,
  Stack,
  TooltipHost,
  mergeStyleSets,
} from "@fluentui/react";
import { Text } from "@fluentui/react/lib/Text";
import ArchivedResourcesGrid from "../components/archived-resources-grid";
import { ArchivedChangeType } from "../enums";
import { sort } from "fast-sort";

// ── Types ─────────────────────────────────────────────────────────────────────
interface SortColumn {
  key: keyof archivedSite;
  label: string;
  icon: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const SORT_COLUMNS: SortColumn[] = [
  { key: "name", label: "Name", icon: "TextField" },
  { key: "isConnected", label: "Type", icon: "ViewAll" },
  { key: "archivedUntil", label: "Archived Until", icon: "Calendar" },
];

// ── Styles ────────────────────────────────────────────────────────────────────
const pageStyles = mergeStyleSets({
  pageWrapper: {
    minHeight: "100vh",
    backgroundColor: "#f8f8f8",
  },
  contentArea: {
    padding: "0 24px 40px",
    maxWidth: "1600px",
    margin: "0 auto",
  },
  pageHeader: {
    padding: "28px 0 20px",
    borderBottom: "1px solid #edebe9",
    marginBottom: "24px",
  },
  pageTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#201f1e",
    margin: 0,
    lineHeight: "32px",
  },
  pageSubtitle: {
    fontSize: "14px",
    color: "#605e5c",
    marginTop: "4px",
    marginBottom: 0,
  },

  // ── Sticky toolbar ──────────────────────────────────────────────────────
  stickyWrapper: {
    position: "sticky",
    top: 0,
    zIndex: 200,
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    background: "rgba(248,248,248,0.95)",
    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    paddingLeft: "10px",
    paddingRight: "10px",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 0",
    flexWrap: "wrap" as const,
  },
  toolbarDivider: {
    width: "1px",
    height: "28px",
    background: "#edebe9",
    flexShrink: 0,
  },
  resultCount: {
    marginLeft: "auto",
    fontSize: "13px",
    color: "#605e5c",
    whiteSpace: "nowrap" as const,
  },

  // ── Sort bar ────────────────────────────────────────────────────────────
  sortBar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 0 10px",
    flexWrap: "wrap" as const,
    borderTop: "1px solid #edebe9",
  },
  sortLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#a19f9d",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    marginRight: "4px",
    whiteSpace: "nowrap" as const,
  },
  sortChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 10px",
    borderRadius: "14px",
    border: "1px solid #e1dfdd",
    background: "#ffffff",
    cursor: "pointer",
    fontSize: "12px",
    color: "#323130",
    transition: "all 0.15s ease",
    userSelect: "none" as const,
    selectors: {
      ":hover": {
        background: "#f3f2f1",
        borderColor: "#c8c6c4",
      },
    },
  },
  sortChipActive: {
    background: "#eff6fc",
    border: "1px solid #0078d4",
    color: "#0078d4",
    fontWeight: 600,
  },

  // ── Loading card ────────────────────────────────────────────────────────
  loadingCard: {
    background: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
    border: "1px solid #edebe9",
    padding: "28px 32px",
    margin: "8px 0 20px",
  },
  shimmerWrapper: {
    opacity: 0.6,
    marginTop: "16px",
  },
  warningBox: {
    marginTop: "20px",
  },
});

// ── Sort chip ─────────────────────────────────────────────────────────────────
interface SortChipProps {
  col: SortColumn;
  isActive: boolean;
  isAsc: boolean;
  onClick: (key: keyof archivedSite) => void;
}
const SortChip: React.FC<SortChipProps> = ({
  col,
  isActive,
  isAsc,
  onClick,
}) => (
  <TooltipHost
    content={
      isActive
        ? `Sorted by ${col.label} (${isAsc ? "A → Z" : "Z → A"}) — click to reverse`
        : `Sort by ${col.label}`
    }
    directionalHint={DirectionalHint.bottomCenter}
  >
    <button
      type="button"
      aria-pressed={isActive}
      aria-label={`Sort by ${col.label}${isActive ? `, currently ${isAsc ? "ascending" : "descending"}` : ""}`}
      className={`${pageStyles.sortChip} ${isActive ? pageStyles.sortChipActive : ""}`}
      onClick={() => onClick(col.key)}
    >
      <Icon
        iconName={col.icon}
        styles={{ root: { fontSize: 12 } }}
        aria-hidden
      />
      {col.label}
      {isActive && (
        <Icon
          iconName={isAsc ? "SortUp" : "SortDown"}
          styles={{ root: { fontSize: 11, marginLeft: 2 } }}
          aria-hidden
        />
      )}
    </button>
  </TooltipHost>
);

// ── Loading card ──────────────────────────────────────────────────────────────
const LoadingCard: React.FC = () => (
  <div
    className={pageStyles.loadingCard}
    role="status"
    aria-live="polite"
    aria-label="Loading archived workspaces"
  >
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
      <Spinner size={SpinnerSize.large} />
      <Stack tokens={{ childrenGap: 2 }}>
        <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
          Loading your archived workspaces…
        </Text>
        <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
          We are retrieving your archived SharePoint sites. This may take a
          moment.
        </Text>
      </Stack>
    </Stack>

    <div style={{ marginTop: "20px", marginBottom: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
          Loading Progress
        </Text>
        <Text
          variant="small"
          styles={{ root: { color: "#0078d4", fontWeight: 600 } }}
        >
          In progress…
        </Text>
      </div>
      <ProgressIndicator
        barHeight={8}
        styles={{
          progressBar: {
            borderRadius: "4px",
            background: "linear-gradient(90deg, #0078d4 0%, #106ebe 100%)",
          },
          progressTrack: { borderRadius: "4px" },
        }}
      />
    </div>

    <div className={pageStyles.shimmerWrapper}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Shimmer
          key={i}
          shimmerElements={[
            { type: ShimmerElementType.circle, height: 28 },
            { type: ShimmerElementType.gap, width: "1%", height: 28 },
            { type: ShimmerElementType.line, height: 28 },
            { type: ShimmerElementType.gap, width: "5%", height: 28 },
          ]}
          styles={{ root: { marginBottom: "8px" } }}
        />
      ))}
    </div>

    <div className={pageStyles.warningBox}>
      <MessageBar
        messageBarType={MessageBarType.warning}
        isMultiline
        styles={{ root: { borderRadius: "4px" } }}
      >
        <Text variant="small">
          <strong>Please do not refresh the page.</strong>
          <br />
          Loading your archived workspaces can take a moment depending on how
          many you own. Your data is on its way!
        </Text>
      </MessageBar>
    </div>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────
const ArchivedResourcesList: React.FunctionComponent = () => {
  const { instance, inProgress, accounts } = useMsal();

  const [baseResourceList, setBaseResourceList] =
    useState<Array<archivedSite> | null>(null);
  const [currentResourceList, setCurrentResourceList] = useState<
    Array<archivedSite>
  >([]);
  const [resourceLoadComplete, toggleResourceLoadComplete] =
    useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedSortColumn, setSelectedSortColumn] =
    useState<keyof archivedSite>("name");
  const [useAscendantSort, setUseAscendantSort] = useState<boolean>(true);

  const loadingIsComplete = React.useRef<boolean>(false);

  // ── Sort chip handler ───────────────────────────────────────────────────────
  const handleSortChipClick = (key: keyof archivedSite): void => {
    if (key === selectedSortColumn) {
      setUseAscendantSort((prev) => !prev);
    } else {
      setSelectedSortColumn(key);
      setUseAscendantSort(true);
    }
  };

  // ── Re-apply sort whenever sort state changes ───────────────────────────────
  useEffect(() => {
    applySearch(searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSortColumn, useAscendantSort]);

  // ── Search helpers ──────────────────────────────────────────────────────────
  function clearSearch(): void {
    setSearchTerm("");
    if (baseResourceList) {
      setCurrentResourceList(applySorting([...baseResourceList]));
    }
  }

  function applySorting(list: Array<archivedSite>): Array<archivedSite> {
    const sortDir = useAscendantSort ? "asc" : "desc";
    return sort(list)[sortDir]((item) => item[selectedSortColumn]);
  }

  function applySearch(criteria: string): void {
    if (!baseResourceList) return;
    setSearchTerm(criteria);
    let filtered = [...baseResourceList];
    if (criteria.trim() !== "") {
      filtered = filtered.filter((item) =>
        item.name!.toLowerCase().includes(criteria.toLowerCase()),
      );
    }
    setCurrentResourceList(applySorting(filtered));
  }

  // ── Data fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchData() {
      try {
        setBaseResourceList([]);

        const RMTAccessTokenRequest = {
          scopes: apiScopes.resourceManagementScopes,
          account: accounts[0],
        };
        const RMTAccessTokenResponse = await instance.acquireTokenSilent(
          RMTAccessTokenRequest,
        );

        const archivedResourceList = await getOwnedArchivedResources(
          RMTAccessTokenResponse.accessToken,
        );

        archivedResourceList.forEach((element) => {
          const baseOwners = element.ownersDetail
            .split(";")
            .map((email) => email.trim());
          const additionalOwners = !element.ownersDetailPending
            ? []
            : element.ownersDetailPending
                .split(";")
                .map((email) => email.trim());

          element.readOnlyOwners =
            element.isConnected === "N" ? baseOwners : [];
          element.ownersList = baseOwners.concat(additionalOwners);
        });

        // apply default sort (name asc) on initial load
        const sorted = sort(archivedResourceList).asc((item) => item.name);
        setBaseResourceList(archivedResourceList);
        setCurrentResourceList(sorted);
        toggleResourceLoadComplete(true);
        loadingIsComplete.current = true;
      } catch (ex) {
        if (!loadingIsComplete.current) {
          toggleResourceLoadComplete(true);
          loadingIsComplete.current = true;
          Swal.fire(
            errorsTypes.somethingWentWrong.errorTitle,
            errorsTypes.somethingWentWrong.errorSubtitle,
            "error",
          );
        }
      }
    }

    if (!baseResourceList && inProgress === InteractionStatus.None) {
      fetchData();
    }
  }, [accounts, baseResourceList, inProgress, instance, resourceLoadComplete]);

  // ── Dataset refresh ─────────────────────────────────────────────────────────
  const refreshMainDataset = (
    resourceID: string = "",
    changeType: ArchivedChangeType,
    value: any,
  ): void => {
    toggleResourceLoadComplete(false);

    let filterResourceList: Array<archivedSite> = [
      ...(baseResourceList as archivedSite[]),
    ];

    filterResourceList.forEach((element) => {
      if (element.id === resourceID) {
        if (changeType === ArchivedChangeType.AddOwner) {
          element.ownersList.push(value as string);
        }
        if (changeType === ArchivedChangeType.RemoveOwner) {
          element.ownersList = element.ownersList.filter(
            (owner) => owner !== (value as string),
          );
        }
        if (changeType === ArchivedChangeType.UpdateRetention) {
          const currentDate = new Date(element.archivedUntil);
          currentDate.setFullYear(
            currentDate.getFullYear() + (value as number),
          );
          element.archivedUntil = currentDate;
        }
      }
    });

    if (changeType === ArchivedChangeType.DeArchive) {
      filterResourceList = filterResourceList.filter(
        (o) => o.id !== resourceID,
      );
    }

    setBaseResourceList(filterResourceList);
    setCurrentResourceList(applySorting(filterResourceList));
    toggleResourceLoadComplete(true);
  };

  // ── Derived values ──────────────────────────────────────────────────────────
  const resultCountLabel =
    currentResourceList.length === 1
      ? "1 archived workspace"
      : `${currentResourceList.length} archived workspaces`;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={pageStyles.pageWrapper}>
      <MainNavbar
        userName={window.CURRENT_USER_EMAIL}
        userActivity={loadFromLocalStorage<activityLog>("userActivityData")}
      />

      <div className={pageStyles.contentArea}>
        <div className={pageStyles.pageHeader}>
          <h1 className={pageStyles.pageTitle}>Archived Workspaces</h1>
          <p className={pageStyles.pageSubtitle}>
            Review and manage workspaces that have been archived. You can
            restore or extend retention from here.
          </p>
        </div>

        <AuthenticatedTemplate>
          {/* ── Sticky toolbar ───────────────────────────────────────────── */}
          <div className={pageStyles.stickyWrapper}>
            {/* Row 1 — search + count */}
            <div className={pageStyles.toolbar}>
              <TooltipHost content="Type a workspace name and press Enter to search">
                <SearchBox
                  placeholder="Search archived workspaces…"
                  onClear={clearSearch}
                  value={searchTerm}
                  styles={{
                    root: { width: 320, maxWidth: "100%", border: "none" },
                  }}
                  onSearch={(newValue: string) => {
                    if (newValue && newValue.trim().length >= 2) {
                      applySearch(newValue.trim());
                    } else if (newValue === "") {
                      clearSearch();
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (newValue === "" || newValue === undefined) {
                      clearSearch(); // box cleared → reset list
                    } else {
                      setSearchTerm(newValue); // ✅ keep controlled input in sync
                      applySearch(newValue); // ✅ live filter as user types
                    }
                  }}
                />
              </TooltipHost>

              <div className={pageStyles.toolbarDivider} aria-hidden="true" />

              {resourceLoadComplete && (
                <Text className={pageStyles.resultCount}>
                  {resultCountLabel}
                </Text>
              )}
            </div>

            {/* Row 2 — sort chips (only when data is loaded) */}
            {resourceLoadComplete && (
              <div
                className={pageStyles.sortBar}
                role="group"
                aria-label="Sort options"
              >
                <span className={pageStyles.sortLabel}>Sort:</span>
                {SORT_COLUMNS.map((col) => (
                  <SortChip
                    key={col.key}
                    col={col}
                    isActive={selectedSortColumn === col.key}
                    isAsc={useAscendantSort}
                    onClick={handleSortChipClick}
                  />
                ))}
              </div>
            )}
          </div>
          {/* ── End sticky toolbar ───────────────────────────────────────── */}

          {!resourceLoadComplete && <LoadingCard />}

          {resourceLoadComplete && (
            <ArchivedResourcesGrid
              ownedResources={currentResourceList}
              refreshDatasetFunction={refreshMainDataset}
            />
          )}
        </AuthenticatedTemplate>

        <UnauthenticatedTemplate>
          <MessageBar
            messageBarType={MessageBarType.warning}
            styles={{ root: { borderRadius: "6px", marginTop: "24px" } }}
          >
            You are not signed in. Please sign in to view your archived
            workspaces.
          </MessageBar>
        </UnauthenticatedTemplate>
      </div>
    </div>
  );
};

export default ArchivedResourcesList;
